import { MessageCircle, MoreVertical, Trash2 } from 'lucide-react'
import { useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import clientApplicationRequest from '@fingerprinting/api/client/application'
import { APPLICATION_QUERY_KEY } from '@fingerprinting/features/client/application/queries'
import { STAFF_APPLICATION_LIST_QUERY_KEY } from '@fingerprinting/features/staff/application/list/hooks/useStaffApplicationList'
import type { TClientApplicationDetail } from '@fingerprinting/types/schemas/application.schema'

interface ActionsCellProps {
  row: { original: TClientApplicationDetail }
}

export function ActionsCell({ row }: ActionsCellProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const applicationId = String(row.original.id)

  const { mutate: deleteApplication, isPending: isDeleting } = useMutation({
    mutationFn: (applicationId: string) =>
      clientApplicationRequest.deleteApplication(applicationId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STAFF_APPLICATION_LIST_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: [...APPLICATION_QUERY_KEY.all, 'overview'] }),
      ])
      toast.success('Application deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete application')
    },
  })

  const handleChat = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      void navigate({
        to: '/fingerprinting/staff/application/$applicationId',
        params: { applicationId },
        search: { chat: 'true' },
      })
    },
    [navigate, applicationId]
  )

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
        deleteApplication(applicationId)
      }
    },
    [deleteApplication, applicationId]
  )

  return (
    <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-md"
            disabled={isDeleting}
          >
            <MoreVertical className="size-4 text-[#0F2433]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-36">
          <DropdownMenuItem className="gap-2" onClick={handleChat}>
            <MessageCircle className="size-4 text-[#0F2433]" />
            <span>Chat</span>
          </DropdownMenuItem>
          <DropdownMenuItem className="gap-2 text-[#D14343] focus:text-[#D14343]" onClick={handleDelete} disabled={isDeleting}>
            <Trash2 className="size-4" />
            <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

